import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit, Trash2, Plus, Save, Play } from 'lucide-react';
import api from '../../services/api';
import ConfirmationModal from '../../components/ConfirmationModal';

export default function LecturerExamDetail() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exam, setExam] = useState(null);
  const [components, setComponents] = useState([]);
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState({ highest: null, lowest: null });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // UI state: search for students in the marks list and deleting flag
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Release marks modal state
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [releasing, setReleasing] = useState(false);
  // focused student row (persistently highlighted)
  const [focusedStudentId, setFocusedStudentId] = useState(null);

  // clear focused highlight when search is cleared
  useEffect(() => {
    if (!search || !search.trim()) setFocusedStudentId(null);
  }, [search]);

  useEffect(() => { loadExam(); }, [examId]);

  const loadExam = async () => {
    setLoading(true);
    try {
      // Get exams list and find exam info
      const examsRes = await api.get('/lecturer/exams');
      const exams = examsRes.data.data?.exams || examsRes.data.exams || [];
      const found = exams.find(e => String(e.exam_id) === String(examId));
      if (found) setExam(found);

      // Get marks + components
      const res = await api.get(`/lecturer/exams/${examId}/marks`);
      if (res.data.success) {
        const comps = res.data.data.components || [];
        const studs = res.data.data.students || [];
        setComponents(comps);
        setStudents(studs);
        // compute stats (exclude zero totals when computing lowest so absentees don't affect lowest)
        const totals = studs.map(s => parseFloat(s.total_marks) || 0);
        const highest = totals.length ? Math.max(...totals) : null;
        const nonZeroTotals = totals.filter(t => t > 0);
        const lowest = nonZeroTotals.length ? Math.min(...nonZeroTotals) : null;
        const average = totals.length ? (totals.reduce((a,b) => a + b, 0) / totals.length) : null;
        setStats({ highest, lowest, average });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load exam details');
    } finally {
      setLoading(false);
    }
  };

  // Helper: fuzzy matching + support for last-name,first-name input
  const matchesStudent = (student, query) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return true;
    const name = (student.full_name || '').toLowerCase();
    const id = (student.student_id_number || '').toLowerCase();

    // If comma present, treat as "last, first" or "last,first"
    if (q.includes(',')) {
      const parts = q.split(',').map(p => p.trim()).filter(Boolean);
      const last = parts[0] || '';
      const first = parts[1] || '';
      if (first && last) return name.includes(first) && name.includes(last);
      return name.includes(last) || name.includes(first);
    }

    const tokens = q.split(/\s+/).filter(Boolean);
    const isSubsequence = (pattern, text) => {
      // simple fuzzy: check if pattern characters appear in text in order
      let i = 0, j = 0;
      while (i < pattern.length && j < text.length) {
        if (pattern[i] === text[j]) i++;
        j++;
      }
      return i === pattern.length;
    };

    return tokens.every(t => name.includes(t) || id.includes(t) || isSubsequence(t, name));
  };

  const deleteExam = async () => {
    if (!confirm('Delete this exam? This will remove marks and data.')) return;
    try {
      setDeleting(true);
      await api.delete(`/lecturer/exams/${examId}`);
      navigate('/lecturer/exams');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete exam');
    } finally {
      setDeleting(false);
    }
  };

  // Release marks for students marked AB (no marks entered) - set all their components to 0 and save
  const releaseMarks = async () => {
    try {
      setReleasing(true);
      setError('');
      setSuccess('');

      // Find students who are considered AB: either no component_marks or all marks are blank ('' or null)
      const absentStudents = students.filter(s => {
        const cm = s.component_marks || [];
        if (cm.length === 0) return true; // no marks at all
        // if all entries are blank or null, treat as AB
        const anyEntered = cm.some(m => m.marks_obtained !== '' && m.marks_obtained !== null && m.marks_obtained !== undefined);
        return !anyEntered;
      });

      if (absentStudents.length === 0) {
        setSuccess('No absent students to release marks for');
        return;
      }

      // For each absent student, ensure component_marks contains a mark for each component with 0
      let processed = 0;
      for (const stu of absentStudents) {
        const cm = stu.component_marks ? [...stu.component_marks] : [];
        for (const c of components) {
          const existing = cm.find(x => Number(x.component_id) === Number(c.component_id));
          if (existing) {
            existing.marks_obtained = 0;
          } else {
            cm.push({ component_id: c.component_id, marks_obtained: 0 });
          }
        }

        // Optimistically update local state
        setStudents(prev => prev.map(p => p.student_id === stu.student_id ? { ...p, component_marks: cm, total_marks: cm.reduce((s, m) => s + (Number(m.marks_obtained) || 0), 0) } : p));

        // Save via API using existing saveStudentMarks helper
        try {
          // reuse saveStudentMarks which expects component marks on student; call with new cm
          await saveStudentMarks(stu.student_id, cm);
          processed++;
        } catch (err) {
          // continue processing other students
        }
      }

      setSuccess(`Released marks for ${processed} student(s)`);
      // Refresh full data
      await loadExam();
    } catch (err) {
      setError('Failed to release marks');
    } finally {
      setReleasing(false);
      setShowReleaseModal(false);
    }
  };

  const saveStudentMarks = async (studentId, componentMarks) => {
    try {
      // Only send components where marks have been explicitly entered (allow 0 but skip blank '')
      // normalize and round marks to 2 decimals to avoid unexpected float behavior
      const componentsToSend = (componentMarks || []).map(cm => ({
        componentId: cm.component_id,
        marksObtained: cm.marks_obtained === '' || cm.marks_obtained === null ? null : Math.round(Number(cm.marks_obtained) * 100) / 100
      })).filter(c => c.marksObtained !== null && !isNaN(c.marksObtained));

      if (componentsToSend.length === 0) {
        setError('No marks entered for student');
        setTimeout(() => setError(''), 2000);
        return;
      }

      const totalMarks = componentsToSend.reduce((sum, c) => sum + c.marksObtained, 0);

      // Log what's being sent for debugging (helps investigate off-by-one issues)
      // Optimistically update local state for immediate feedback
      const updatedStudents = students.map(s => {
        if (s.student_id === studentId) {
          const newComponentMarks = s.component_marks ? [...s.component_marks] : [];
          componentsToSend.forEach(c => {
            const existing = newComponentMarks.find(mm => mm.component_id === c.componentId);
            if (existing) existing.marks_obtained = c.marksObtained;
            else newComponentMarks.push({ component_id: c.componentId, marks_obtained: c.marksObtained });
          });
          return { ...s, component_marks: newComponentMarks, total_marks: totalMarks };
        }
        return s;
      });
      setStudents(updatedStudents);

      // recompute stats locally (exclude zeros for lowest)
      const totals = updatedStudents.map(s => parseFloat(s.total_marks) || 0);
      const highest = totals.length ? Math.max(...totals) : null;
      const nonZeroTotals = totals.filter(t => t > 0);
      const lowest = nonZeroTotals.length ? Math.min(...nonZeroTotals) : null;
      const average = totals.length ? (totals.reduce((a,b) => a + b, 0) / totals.length) : null;
      setStats({ highest, lowest, average });

      const res = await api.post(`/lecturer/exams/${examId}/marks`, { marks: [{ studentId, totalMarks, components: componentsToSend }] });
      setSuccess('Marks saved');
      setTimeout(() => setSuccess(''), 2000);

      // Refresh from server to ensure canonical data
      loadExam();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save marks');
    }
  };

  const onComponentInput = (studentId, compId, rawValue) => {
    const newStudents = [...students];
    const idx = newStudents.findIndex(s => s.student_id === studentId);
    if (idx === -1) return;
    const student = newStudents[idx];
    if (!student.component_marks) student.component_marks = [];
    const mark = student.component_marks.find(m => m.component_id === compId);
    // allow blank '' to mean no change
    const val = rawValue === '' ? '' : (isNaN(Number(rawValue)) ? '' : Number(rawValue));
    if (!mark) student.component_marks.push({ component_id: compId, marks_obtained: val });
    else mark.marks_obtained = val;
    setStudents(newStudents);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate('/lecturer/exams')} className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium mb-2">
            <span className="inline-flex items-center justify-center w-8 h-8 bg-indigo-50 text-indigo-600 rounded-full"><svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/></svg></span>
            Back to exams
          </button>

          <h1 className="text-2xl font-bold">{exam?.exam_name || 'Exam'}</h1>
          <p className="text-gray-500 mt-1">{exam?.grade_name} — {exam?.exam_date ? new Date(exam.exam_date).toLocaleDateString() : '-'}</p>
        </div>

        <div className="flex items-center gap-3">
          <button type="button" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg" onClick={() => navigate('/lecturer/exams', { state: { editExamId: String(examId) } })}><Edit className="w-4 h-4" /> Edit</button>
          <button type="button" className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg font-semibold shadow-sm" onClick={() => setShowReleaseModal(true)} disabled={releasing}><Play className="w-4 h-4" /> Release marks</button>
          <button type="button" disabled={deleting} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${deleting ? 'bg-red-100 text-red-400' : 'bg-red-50 text-red-600'}`} onClick={deleteExam}>{deleting ? 'Deleting...' : <><Trash2 className="w-4 h-4" /> Delete</>}</button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded">{error}</div>}
      {success && <div className="bg-green-50 text-green-700 p-3 rounded">{success}</div>}

      {/* Release Marks Confirmation Modal */}
      <ConfirmationModal
        isOpen={showReleaseModal}
        onClose={() => setShowReleaseModal(false)}
        onConfirm={() => releaseMarks()}
        title="Release marks for absent students"
        message={`This will set all component marks to 0 for students who are currently marked AB (no marks entered) in this exam.\n\nThis action cannot be undone. Do you want to continue?`}
        confirmText={releasing ? 'Processing...' : 'Confirm'}
        cancelText="Cancel"
        type="warning"
      />
      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col">
          <div className="text-sm text-gray-500">Highest</div>
          <div className="text-2xl font-bold text-indigo-700 mt-2">{stats.highest !== null ? stats.highest : '-'}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col">
          <div className="text-sm text-gray-500">Average</div>
          <div className="text-2xl font-bold text-green-600 mt-2">{stats.average !== null ? stats.average.toFixed(2) : '-'}</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-6 flex flex-col">
          <div className="text-sm text-gray-500">Lowest</div>
          <div className="text-2xl font-bold text-gray-800 mt-2">{stats.lowest !== null ? stats.lowest : '-'}</div>
        </div>
      </div>

      {/* Components / Breakdown */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Mark Components</h3>
          <div className="text-sm px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full">Total: {components.reduce((s,c) => s + (parseInt(c.max_marks) || 0), 0)} marks</div>
        </div>
        <div className="space-y-2">
          {components.map(c => (
            <div key={c.component_id} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-700">{c.component_name}</div>
              <div className="text-sm font-semibold text-indigo-600">{c.max_marks} marks</div>
            </div>
          ))}
          {components.length === 0 && <div className="text-sm text-gray-400">No components defined</div>}
        </div>
      </div>

      {/* Students list */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Student Marks</h3>
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-500">{(students.filter(s => {
              const q = search.trim().toLowerCase();
              if (!q) return true;
              return (s.full_name||'').toLowerCase().includes(q) || (s.student_id_number||'').toLowerCase().includes(q);
            })).length} students</div>
            <div className="relative">
              <input
                type="search"
                placeholder="Search students..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const filtered = students.filter(s => matchesStudent(s, search));
                    if (filtered.length === 1) {
                      const id = filtered[0].student_id;
                      setFocusedStudentId(id);
                      const el = document.getElementById(`student-row-${id}`);
                      if (el) {
                        const input = el.querySelector('input[type="number"]');
                        if (input) input.focus();
                        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }
                  }
                }}
                className="px-3 py-2 border rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="pb-2">Student</th>
                {components.map(c => <th key={c.component_id} className="pb-2">{c.component_name}</th>)}
                <th className="pb-2">Total</th>
                <th className="pb-2">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(() => {
                const q = search.trim();
                const filteredStudents = q ? students.filter(s => matchesStudent(s, q)) : students;
                return filteredStudents.map((stu, idx) => {
                  const studentMarks = stu.component_marks || [];
                  const total = studentMarks.reduce((sum, m) => sum + (m.marks_obtained === '' ? 0 : (Number(m.marks_obtained) || 0)), 0);
                  const isAbsent = total === 0;
                  const focused = Number(focusedStudentId) === Number(stu.student_id);
                  return (
                    <tr id={`student-row-${stu.student_id}`} key={stu.student_id} className={`${isAbsent ? 'bg-red-50' : 'hover:bg-gray-50'} ${focused ? 'ring-2 ring-indigo-300 ring-offset-2 bg-indigo-50' : ''}`}>
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-semibold">{stu.full_name?.charAt(0)}</div>
                          <div>
                            <div className="font-medium text-gray-800">{stu.full_name}</div>
                            <div className="text-xs text-gray-500">{stu.student_id_number || ''}</div>
                          </div>
                        </div>
                      </td>
                      {components.map(c => {
                        const markObj = (studentMarks || []).find(m => m.component_id === c.component_id);
                        const value = markObj ? (markObj.marks_obtained === '' ? '' : markObj.marks_obtained) : '';
                        return (
                          <td key={c.component_id} className="py-2">
                            <input
                              type="number"
                              min="0"
                              max={c.max_marks}
                              value={value}
                              onFocus={() => setFocusedStudentId(stu.student_id)}
                              onChange={(e) => onComponentInput(stu.student_id, c.component_id, e.target.value)}
                              className="w-20 px-2 py-1 border rounded-md"
                              placeholder=""
                            />
                          </td>
                        );
                      })}
                      <td className="py-2 font-semibold text-gray-800">{isAbsent ? <span className="text-red-600">AB</span> : total}</td>
                      <td className="py-2">
                        <button type="button" onClick={() => saveStudentMarks(stu.student_id, stu.component_marks || [])} className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-md shadow-sm">Save</button>
                      </td>
                    </tr>
                  );
                });
              })()}
              {(() => {
                const q = search.trim().toLowerCase();
                const filteredStudents = q ? students.filter(s => (s.full_name||'').toLowerCase().includes(q) || (s.student_id_number||'').toLowerCase().includes(q)) : students;
                if (filteredStudents.length === 0) {
                  return (<tr><td colSpan={components.length + 3} className="px-4 py-8 text-center text-gray-400">No students match your search</td></tr>);
                }
                return null;
              })()}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}